import type {
  CarrierCustomerConnection,
  CarrierCustomerTier,
  CarrierLabelDiscountRule,
} from "@/api/carrier";
import { formatPercent } from "@/lib/format";
import { customerOverrideRule } from "./discount-rule-resolution";

export const DEFAULT_CUSTOMER_TIER_KEY = "__default__";
export type CustomerDiscountsMode = "customers" | "discounts";

export function formatOptionalPercent(
  value: number | null | undefined
): string {
  return value == null ? "-" : formatPercent(value);
}

export function customerName(row: CarrierCustomerConnection): string {
  const { firstName, lastName, email } = row.customer;
  const name = [firstName, lastName].filter(Boolean).join(" ").trim();
  return name || email || "Unknown customer";
}

export function customerStatus(
  row: CarrierCustomerConnection
): "ACTIVE" | "BLOCKED" | "LEFT" {
  return row.removed ? "LEFT" : row.status;
}

export function customerSearchText(row: CarrierCustomerConnection): string {
  return [
    customerName(row),
    row.customer.email,
    row.tier?.name,
    row.status,
    row.removed ? "left removed" : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export interface CustomerViewModel {
  name: string;
  email: string;
  status: ReturnType<typeof customerStatus>;
  tierName: string;
  effectiveDiscount: string;
  overrideRule: CarrierLabelDiscountRule | null;
}

export function customerViewModel(
  row: CarrierCustomerConnection,
  tiers: CarrierCustomerTier[],
  rules: CarrierLabelDiscountRule[] = []
): CustomerViewModel {
  return {
    name: customerName(row),
    email: row.customer.email ?? "Email unavailable",
    status: customerStatus(row),
    tierName:
      row.tier?.name ??
      tiers.find((tier) => tier.id === row.tierId)?.name ??
      "Default",
    effectiveDiscount: formatOptionalPercent(row.effectiveDiscountPercentage),
    overrideRule: customerOverrideRule(rules, row.customerId, row.tierId),
  };
}
